import { useState, type FormEvent, useEffect } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { CheckCircle } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { LoadingSpinner } from "../../components/shared/LoadingSpinner";
import { httpsCallable } from "firebase/functions";
import { useAuth } from "../../hooks/useAuth";
import { functions } from "../../lib/firebase";
import logo from "../../assets/logo.png";

const features = [
  "Structured offboarding checklists",
  "Knowledge capture before the last day",
  "Access revocation tracking",
];

interface InviteData {
  email: string;
  companyName: string;
  role: string;
  invitedByName: string;
}

type InvitePreview =
  | ({ valid: true } & InviteData)
  | { valid: false; reason: "not_found" | "used" | "expired" };

const INVITE_ERRORS: Record<"not_found" | "used" | "expired", string> = {
  not_found: "We couldn't find that invite. Ask your admin to send a new one.",
  used: "This invite has already been used. Try signing in instead.",
  expired: "This invite has expired. Ask your admin to send a new one.",
};

const ROLE_LABELS: Record<string, string> = {
  hr_admin: "HR Admin",
  it_admin: "IT Admin",
  manager: "Manager",
};

export default function Signup() {
  const { user, loading, companyId, signUpWithEmail, signInWithGoogle } = useAuth();
  const [searchParams] = useSearchParams();
  const inviteId = searchParams.get("invite");
  const [inviteData, setInviteData] = useState<InviteData | null>(null);
  const [inviteError, setInviteError] = useState("");
  const [inviteLoading, setInviteLoading] = useState(Boolean(inviteId));
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // The invites collection is not client-readable — reading it straight from
  // here always failed with "Missing or insufficient permissions", because the
  // invitee is not signed in yet. previewInvite is the server-side lookup that
  // returns only what this page renders.
  useEffect(() => {
    if (!inviteId) return;
    let cancelled = false;

    const loadInvite = async () => {
      setInviteLoading(true);
      try {
        const previewInvite = httpsCallable<{ inviteId: string }, InvitePreview>(
          functions,
          "previewInvite"
        );
        const { data } = await previewInvite({ inviteId });
        if (cancelled) return;

        if (!data.valid) {
          setInviteError(INVITE_ERRORS[data.reason] ?? INVITE_ERRORS.not_found);
          return;
        }
        setInviteData(data);
        setEmail(data.email);
        setInviteError("");
      } catch (err) {
        console.error("Failed to load invite:", err);
        if (!cancelled) {
          setInviteError("We couldn't load this invite. Please try the link again.");
        }
      } finally {
        if (!cancelled) setInviteLoading(false);
      }
    };

    loadInvite();
    return () => {
      cancelled = true;
    };
  }, [inviteId]);

  if (loading) return <LoadingSpinner fullScreen />;
  if (user && companyId) return <Navigate to="/dashboard" replace />;
  if (user && !companyId) return <Navigate to="/setup" replace />;

  const handleSignup = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    if (!name || !email || !password) {
      setError("Please fill in all fields.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (!agreedToTerms) {
      setError("Please agree to the terms and conditions.");
      return;
    }

    if (inviteData && email.toLowerCase() !== inviteData.email.toLowerCase()) {
      setError(`Please sign up with ${inviteData.email}`);
      return;
    }

    setSubmitting(true);
    try {
      await signUpWithEmail(email, password, name);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Sign up failed.";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError("");
    setSubmitting(true);
    try {
      // On an invite, Google must offer the invited address — membership is
      // matched server-side on the verified token email.
      await signInWithGoogle(inviteData?.email);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Google sign in failed.";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden">
      <div className="hidden lg:flex lg:w-[60%] bg-navy flex-col justify-center px-16 xl:px-24">
        <h1 className="font-display text-4xl xl:text-5xl text-white leading-tight">
          Offboarding, done right.
        </h1>
        <p className="mt-4 text-mist text-lg max-w-md">
          Join hundreds of HR teams using OffboardSet to create smooth,
          structured employee exits.
        </p>
        <ul className="mt-10 space-y-4">
          {features.map((feature) => (
            <li key={feature} className="flex items-center gap-3 text-white/90">
              <CheckCircle size={20} className="text-teal flex-shrink-0" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center bg-warm px-6 overflow-y-auto py-8">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-8">
            <img src={logo} alt="OffboardSet Logo" className="w-8 h-8 object-contain" />
            <span className="font-display text-xl text-navy">OffboardSet</span>
          </div>

          <h2 className="text-2xl font-semibold text-navy mb-1">
            {inviteId ? "Accept your invite" : "Start your free account"}
          </h2>
          <p className="text-sm text-mist mb-8">
            {inviteLoading
              ? "Checking your invitation…"
              : inviteData
              ? `Join ${inviteData.companyName} as ${ROLE_LABELS[inviteData.role] ?? inviteData.role.replace(/_/g, " ")}`
              : inviteId && inviteError
              ? inviteError
              : "Set up in 5 minutes. No credit card required."}
          </p>

          {(error || inviteError) && (
            <div className="mb-4 p-3 bg-ember/10 border border-ember/20 rounded-md text-sm text-ember">
              {error || inviteError}
            </div>
          )}

          {inviteData && (
            <div className="mb-6 p-3 bg-teal/5 border border-teal/20 rounded-md text-sm">
              <p className="text-teal font-medium">Invited by {inviteData.invitedByName}</p>
              <p className="text-teal/70 text-xs mt-1">to join {inviteData.companyName}</p>
            </div>
          )}

          <Button
            variant="secondary"
            fullWidth
            size="lg"
            onClick={handleGoogleSignIn}
            disabled={submitting || inviteLoading}
          >
            <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            Continue with Google
          </Button>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-navy/10" />
            <span className="text-xs text-mist">or</span>
            <div className="flex-1 h-px bg-navy/10" />
          </div>

          <form onSubmit={handleSignup} className="space-y-4">
            <Input
              label="Full Name"
              type="text"
              placeholder="Jane Smith"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <Input
              label="Work Email"
              type="email"
              placeholder="jane@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={!!inviteData}
              required
            />
            <Input
              label="Password"
              type="password"
              placeholder="At least 8 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              hint="Must be at least 8 characters"
            />
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="mt-0.5 rounded border-navy/20 text-teal focus:ring-teal/50"
              />
              <span className="text-sm text-mist">
                I agree to the{" "}
                <span className="text-teal">Terms of Service</span> and{" "}
                <span className="text-teal">Privacy Policy</span>
              </span>
            </label>
            <Button
              type="submit"
              fullWidth
              size="lg"
              loading={submitting}
              disabled={inviteLoading}
            >
              Create account
            </Button>
          </form>

          <p className="mt-6 text-sm text-center text-mist">
            Already have an account?{" "}
            <Link to="/login" className="text-teal hover:text-teal-light font-medium">
              Sign in
            </Link>
          </p>

          <p className="mt-4 text-xs text-center text-mist/50">
            A product of{" "}
            <a
              href="https://www.lumoraventures.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-teal transition-colors"
            >
              Lumora Ventures PVT LTD
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
